import { type FC, useEffect } from 'react';
import { Collapse, Form, Radio, Spin } from 'antd';
import type { FormInstance } from 'antd';

import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import ConfigDrivenFormFields from '../../components/ConfigDrivenFormFields';
import type { UseDimOptionsHook } from '../../components/DimSelect';
import useEventAttributeFilters from './hooks/useEventAttributeFilters';
import EventPromoSection from './EventPromoSection';
import { useRuleBoundForm } from '../../rules/store/useRuleBoundForm';

const PANEL_STYLE = { backgroundColor: '#FFF' };
const COLLAPSE_DEFAULT_KEYS: string[] = ['1'];
const SHOW_BLOCK_STYLE = { display: 'block' } as const;
const HIDE_BLOCK_STYLE = { display: 'none' } as const;

type EventActiveView = 'event-details' | 'promo-details';

type EventPromoSummary = {
    promo_id: string;
    promo_name: string;
};

interface EventsSectionProps {
    form: FormInstance;
    isEventSaved?: boolean;
    onAddPromo?: () => void;
    onDeletePromo?: (promoId: string) => void;
    isEditMode?: boolean;
    fetchedFormValues?: Record<string, any> | null;
    isEventDataPending?: boolean;
    eventPromos?: EventPromoSummary[];
    activeEventView: EventActiveView;
    onActiveEventViewChange: (view: EventActiveView) => void;
}

const useEventDimOptions: UseDimOptionsHook = (params) =>
    useEventAttributeFilters({
        dimension: params.dimension,
        attributeType: params.attributeType,
        lowestAttribute: params.lowestAttribute ?? false,
        filters: (params.filters ?? {}) as Record<string, unknown>,
        selectedDimension: params.selectedDimension ?? {
            value: '',
            dimension: params.dimension,
            attributeType: params.attributeType,
        },
        searchString: params.searchString ?? '',
        enabled: params.enabled,
    });

const EventsSection: FC<EventsSectionProps> = ({
    form: eventForm,
    isEventSaved,
    onAddPromo,
    onDeletePromo,
    isEditMode,
    fetchedFormValues: fetchedEventFormValues,
    isEventDataPending,
    eventPromos,
    activeEventView,
    onActiveEventViewChange,
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const isEventDetailsView = activeEventView === 'event-details';
    const isPromoDetailsView = activeEventView === 'promo-details';

    // Rule-engine bridge for the event form. Keeps the event working set in the
    // cross-section store so Promo rules can read it.
    const { onValuesChange: ruleOnValuesChange, syncToStore } = useRuleBoundForm('event', eventForm);

    useEffect(() => {
        if (isEditMode && fetchedEventFormValues) {
            eventForm.setFieldsValue(fetchedEventFormValues);
            syncToStore(fetchedEventFormValues, 'hydrate');
        }
    }, [isEditMode, fetchedEventFormValues, eventForm, syncToStore]);

    return (
        <div className="relative">
            {isEditMode && isEventDataPending && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <Spin size="default" />
                </div>
            )}
            <div className="flex flex-row justify-between gap-x-4">
                <p className="text-base font-medium">
                    {isEventDetailsView
                        ? tpoGenConfig.labels.events.eventDetails
                        : tpoGenConfig.labels.events.productPortfolio}
                </p>
                <Radio.Group value={activeEventView} onChange={(e) => onActiveEventViewChange(e.target.value)}>
                    <Radio.Button value="event-details">
                        <p className={`text-sm ${isEventDetailsView ? 'font-medium' : ''}`}>
                            {tpoGenConfig.labels.events.eventDetails}
                        </p>
                    </Radio.Button>
                    <Radio.Button value="promo-details">
                        <p className={`text-sm ${isPromoDetailsView ? 'font-medium' : ''}`}>
                            {tpoGenConfig.labels.events.productPortfolio}
                        </p>
                    </Radio.Button>
                </Radio.Group>
            </div>
            <Form
                form={eventForm}
                name="event-details"
                onValuesChange={(changed) => ruleOnValuesChange(changed)}
            >
                <div className="my-4">
                    <div style={isEventDetailsView ? SHOW_BLOCK_STYLE : HIDE_BLOCK_STYLE}>
                        <Collapse
                            expandIconPosition="end"
                            defaultActiveKey={COLLAPSE_DEFAULT_KEYS}
                            style={PANEL_STYLE}
                        >
                            <Collapse.Panel
                                header={
                                    <p className="text-base font-semibold">
                                        {isEditMode
                                            ? tpoGenConfig.labels.events.editEventDetails
                                            : tpoGenConfig.labels.events.addEventDetails}
                                    </p>
                                }
                                key="1"
                            >
                                <ConfigDrivenFormFields
                                    fields={tpoGenConfig.eventDetailsFields}
                                    isEditMode={isEditMode}
                                    useDimOptions={useEventDimOptions}
                                />
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                    <div style={isPromoDetailsView ? SHOW_BLOCK_STYLE : HIDE_BLOCK_STYLE}>
                        <Collapse
                            expandIconPosition="end"
                            defaultActiveKey={COLLAPSE_DEFAULT_KEYS}
                            style={PANEL_STYLE}
                        >
                            <Collapse.Panel
                                header={
                                    <p className="text-base font-semibold">
                                        {isEditMode
                                            ? tpoGenConfig.labels.events.editPromoDetails
                                            : tpoGenConfig.labels.events.addPromoDetails}
                                    </p>
                                }
                                key="1"
                            >
                                <ConfigDrivenFormFields
                                    fields={tpoGenConfig.promoDetailsFields}
                                    isEditMode={isEditMode}
                                    useDimOptions={useEventDimOptions}
                                />
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                </div>
            </Form>
            <EventPromoSection
                isEventSaved={isEventSaved}
                promos={eventPromos}
                onAddPromo={onAddPromo}
                onDeletePromo={onDeletePromo}
            />
        </div>
    );
};

EventsSection.displayName = 'EventsSection';
export default EventsSection;
